import { useCallback, useEffect, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { EventItem } from "@/components/event/EventItem";
import { SearchForm } from "@/components/common/SearchForm";
import { Toggle } from "@/components/ui/toggle";
import { Dropdown } from "@/components/ui/select";
import { Trash2, Plus, ArrowDownNarrowWide, ArrowUpWideNarrow } from "lucide-react";
import { SortCategory } from "@/helper/sort";
import { EventInfoSchema } from "@/components/event/EventSchema";
import { sortData } from "@/helper/sort";
import { useApiFetch } from "@/helper/apiFetch";
import { useAuth } from "@/auth";
import { useTranslate } from "@/helper/translate";
import { z } from "zod";
import { useNavigate } from "react-router-dom";


type Lookup = { id: string; name: string };


// Utility: Filter search input
function applySearchFilter(data: EventInfoSchema[], query: string): EventInfoSchema[] {
  const term = query.trim().toLowerCase();
  if (term == '') return data;
  if (!data) return data;
  return data.filter(item =>
    item.Name.toLowerCase().includes(term) ||
    (item.EventPath ?? "").toLowerCase().includes(term)
  );
}

// Component
export function EventsPage() {

  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  const [isLoaded, setIsLoaded] = useState(false);

  const [events, setEvents] = useState<EventInfoSchema[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [sortCategory, setSortCategory] = useState<SortCategory>();
  const [searchText, setSearchText] = useState<string>("");
  const [linksHtml, setLinksHtml] = useState<string>("");

 
  const colHeaders = [{ name: "Live", sort: "" }, { name: "Links", sort: "" }, { name: "Event Name", sort: "name" }, { name: "Event Path", sort: "eventPath" }, { name: "Start Date", sort: "dateFrom" }, { name: "End Date", sort: "dateTo" }, { name: "Start Time", sort: "" }, { name: "End Time", sort: "" }];
  const handleSortCategoryChange = (newCategory: string | number) => {
    const newC: SortCategory = {
      name: newCategory,
      direction: sortCategory?.name === newCategory ? !sortCategory?.direction : true
    }

    setSortCategory(newC);
    setEvents(sortData(events, newC));
  };

  
  const navigate = useNavigate();
  const openEvent = useCallback((id: string, isNew: boolean) => {
    const params = new URLSearchParams({
      eventId: id,
      new: isNew ? "true" : "false"
    });
    navigate(`/event?${params.toString()}`);
  }, [navigate]);
 
  const copyEvent = useCallback((id: string) => {
    const path = window.prompt('Provide a path for the event copy:');
    if (!path) return;
    apiFetch(`/api/EventPool/CopyEvent?eventId=${id}&newpath=${encodeURIComponent(path)}`, { method: "GET" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to copy event");

        var newId = await res.text();
        if (!newId || newId.replaceAll("\"", "") === "") throw new Error("Failed to copy event: No ID returned");
        //navigate to new event
        openEvent(newId.replaceAll("\"", "") , true);
      })
      .catch((err) => {
        console.error(err);
        alert(translate("ui.copyeventfailed"));
      });
  }, [apiFetch, translate, openEvent]);

  const handleSearchSubmit = (value: string) => {
    setSearchText(value);
  };

  const filteredData = applySearchFilter(events, searchText);

  const now = new Date();

   const loadEventLinks = (id: string) => {
  apiFetch(`/Admin/GetEventLinks?eventId=${id}&newCode=true`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(await res.text());
        }

        return res.text();
      })
      .then((html) => {
        setLinksHtml(html);
      })
      .catch((err) => {
        console.error(err);
        alert(translate("ui.loadlinksfailed"));})
     
    }



  const eventItems = filteredData && filteredData.map((item, index) => (
     (!showInactive && new Date(item.DateTo) < now) ? null : (
      <EventItem
        key={index}
        item={item}
        onClick={openEvent}
        onCopyEvent={copyEvent}
        onLinksClicked={loadEventLinks}
      />
    )
  ));



  useEffect(() => {
   
     
      apiFetch(`/api/EventPool/GetAllEvents`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch events");
          return res.json();
        })
        .then((data) => {
          setEvents(data);
          setIsLoaded(true);
          
        })
        .catch((err) => {
          console.error(err);
        });
    

  }, [ showInactive, refreshCount]);

 

  return (
    <div>
      <div className="content-header flex justify-between border-b border-gray-300 pt-[0.6rem] pb-[0.6rem] mb-[0.8rem] ml-4 mr-4">
        <div className="flex items-center gap-4">
          <h1 className="font-bold text-2xl">{translate("ui.events")}</h1>
         
            <Plus className="w-6 h-6 cursor-pointer" onClick={() => openEvent("", true)} />
        
          

        </div>

       



        <div className="flex items-center gap-1">
        <SearchForm title={translate("ui.searchorder")} onSearch={handleSearchSubmit} />
          <Toggle
            title={translate("ui.excludeorders")}
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />


        </div>
      </div>

      {!isLoaded ? (
        <div>Loading…</div>
      ) : (

        <div className="flex-1 overflow-auto">


          <table className="table-auto w-full border-collapse">
            <thead className="border-b border-gray-300  text-sm text-gray-600">
              <tr>
                {colHeaders.map((header, index) => {

          

                  return (
                    <th
                      key={index}
                      className="px-4 py-2 text-left cursor-pointer "
                      onClick={() => handleSortCategoryChange(header.sort)}
                    >
                      <div className=" flex items-center">
                        {header.name}
                        {sortCategory?.name === header.sort ? (
                          sortCategory.direction ? (
                            <ArrowDownNarrowWide className="w-3 h-3" />
                          ) : (
                            <ArrowUpWideNarrow className="w-3 h-3" />
                          )
                        ) : <div className="w-3" />}
                      </div>
                    </th>)
                })}
              </tr>

            </thead>
            <tbody>{eventItems}</tbody>
          </table>
        </div>)}



  <Popup
        isOpen={linksHtml != ""}
        onClose={() => setLinksHtml("")}
        title="Event Links"
        className="w-[95vw] "
      >
        <iframe
              title="Event links"
              srcDoc={linksHtml}
            sandbox="allow-same-origin allow-scripts allow-popups"
              className="min-h-[700px] w-full border-0"
            />
      </Popup>


     
    </div>




    


  );
}
